// solo_ux.ts — Helpers UX pour le mode monoutilisateur (US) et futur Premium.
//
// Mai 2026 — feature/monoutilisateur. Marqueur visuel unique (cadenas
// Lucide) sur les éléments visibles-mais-désactivés en raison de l'offre
// Solo (et plus tard Premium). Le marqueur est cliquable et déclenche un
// bandeau d'upsell (dismissable) en haut de page.
//
// API exposée :
//   - markLocked(el, reason, hint?) : ajoute la classe + l'attribut
//     interceptant le clic. `reason` peut être 'solo' ou 'premium' (futur).
//   - unmarkLocked(el) : retire les attributs/classes.
//   - showUpsellBanner(reason) : ouvre le bandeau ; appelée auto au clic.
//   - dismissUpsellBanner() : ferme le bandeau.
//
// CSS associé : `.ux-locked` (cf. app.css). Le bandeau utilise `.ux-upsell-banner`.

type LockReason = 'solo' | 'premium';

declare global {
  interface Window {
    markLocked?: (el: HTMLElement, reason?: LockReason, hint?: string) => void;
    unmarkLocked?: (el: HTMLElement) => void;
    showUpsellBanner?: (reason?: LockReason) => void;
    dismissUpsellBanner?: () => void;
  }
}

const BANNER_ID = 'ux-upsell-banner';
const DEFAULT_HINT_SOLO = 'Cette fonctionnalité est disponible avec l’offre Équipe.';
const DEFAULT_HINT_PREMIUM = 'Cette fonctionnalité est disponible avec l’offre Premium.';

function _hintFor(reason: LockReason): string {
  return reason === 'premium' ? DEFAULT_HINT_PREMIUM : DEFAULT_HINT_SOLO;
}

function markLocked(el: HTMLElement, reason: LockReason = 'solo', hint?: string): void {
  if (!el || el.classList.contains('ux-locked')) return;
  el.classList.add('ux-locked');
  el.setAttribute('data-lock-reason', reason);
  el.setAttribute('aria-disabled', 'true');
  el.title = hint || _hintFor(reason);
  // Capture le clic en phase capture pour bloquer toute action native du
  // composant (select change, button submit, lien, etc.) avant qu'elle
  // ne se déclenche.
  el.addEventListener('click', _onLockedClick, true);
  el.addEventListener('mousedown', _swallow, true);
  el.addEventListener('keydown', _swallow, true);
  // Les <select> et <input> natifs doivent rester en disabled pour éviter
  // l'édition au focus clavier — le clic est toujours capté.
  const tag = el.tagName;
  if (tag === 'SELECT' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON') {
    (el as HTMLInputElement).disabled = true;
  }
}

function unmarkLocked(el: HTMLElement): void {
  if (!el) return;
  el.classList.remove('ux-locked');
  el.removeAttribute('data-lock-reason');
  el.removeAttribute('aria-disabled');
  el.removeAttribute('title');
  el.removeEventListener('click', _onLockedClick, true);
  el.removeEventListener('mousedown', _swallow, true);
  el.removeEventListener('keydown', _swallow, true);
}

function _swallow(ev: Event): void {
  ev.preventDefault();
  ev.stopPropagation();
}

function _onLockedClick(ev: Event): void {
  ev.preventDefault();
  ev.stopPropagation();
  const target = ev.currentTarget as HTMLElement | null;
  const reason = (target?.getAttribute('data-lock-reason') as LockReason) || 'solo';
  showUpsellBanner(reason);
}

function showUpsellBanner(reason: LockReason = 'solo'): void {
  let banner = document.getElementById(BANNER_ID);
  if (banner) {
    // déjà ouvert : on actualise le reason et on reset l'animation.
    banner.setAttribute('data-lock-reason', reason);
    return;
  }
  banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'ux-upsell-banner';
  banner.setAttribute('role', 'status');
  banner.setAttribute('data-lock-reason', reason);

  const label =
    reason === 'premium'
      ? 'Cette fonctionnalité est disponible avec l’offre Premium.'
      : 'Vous utilisez Ornexia en mode Solo. Découvrez l’offre Équipe pour collaborer.';
  banner.innerHTML = `
    <span class="ux-upsell-banner__text">${label}</span>
    <button type="button" class="ux-upsell-banner__close" aria-label="Fermer">&times;</button>
  `;
  const closeBtn = banner.querySelector<HTMLButtonElement>('.ux-upsell-banner__close');
  if (closeBtn) closeBtn.addEventListener('click', dismissUpsellBanner);
  document.body.prepend(banner);
  // Décale le contenu sous le bandeau (cf. body.ux-upsell-open dans app.css).
  document.body.classList.add('ux-upsell-open');
}

function dismissUpsellBanner(): void {
  const banner = document.getElementById(BANNER_ID);
  if (banner && banner.parentNode) {
    banner.parentNode.removeChild(banner);
  }
  document.body.classList.remove('ux-upsell-open');
}

window.markLocked = markLocked;
window.unmarkLocked = unmarkLocked;
window.showUpsellBanner = showUpsellBanner;
window.dismissUpsellBanner = dismissUpsellBanner;

export {};
